import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Heart,
  Share2,
  ShoppingBag,
} from 'lucide-react';
import { bagsApi, BACKEND_URL } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

const BagDetailPage = () => {
  const { id } = useParams();
  const [bag, setBag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const toImageUrl = (value) => {
    if (!value || typeof value !== 'string') return '/placeholder.png';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    if (value.startsWith('data:image')) return value;
    return `${BACKEND_URL}${value.startsWith('/') ? value : `/${value}`}`;
  };

  const normalizeBag = (data) => {
    const rawBag = data?.bag || data?.data || data;

    if (!rawBag) return null;

    const gallery = Array.isArray(rawBag.images)
      ? rawBag.images
          .map((item) => {
            if (typeof item === 'string') return { img: item };
            if (item && typeof item === 'object' && item.img) return { img: item.img };
            return null;
          })
          .filter(Boolean)
      : [];

    return {
      _id: rawBag._id || rawBag.id || '',
      bagAd: rawBag.bagAd || '',
      bagNov: rawBag.bagNov || '',
      bagPrice: Number(rawBag.bagPrice) || 0,
      bagCategory: rawBag.bagCategory || '',
      bagImg: rawBag.bagImg || '',
      images: gallery,
      createdAt: rawBag.createdAt,
      updatedAt: rawBag.updatedAt,
    };
  };

  const fetchBag = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await bagsApi.getById(id);
      const normalized = normalizeBag(response?.data);

      if (!normalized?._id) {
        throw new Error('Bag not found');
      }

      setBag(normalized);
    } catch (err) {
      console.error('Error fetching bag:', err);
      setError('Məhsul tapılmadı.');
      setBag(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBag();
  }, [id]);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [bag?._id]);

  const images = useMemo(() => {
    if (!bag) return ['/placeholder.png'];

    const list = [];
    const seen = new Set();

    if (bag.bagImg) {
      const main = toImageUrl(bag.bagImg);
      if (!seen.has(main)) {
        list.push(main);
        seen.add(main);
      }
    }

    if (Array.isArray(bag.images)) {
      bag.images.forEach((item) => {
        if (!item?.img) return;
        const imgUrl = toImageUrl(item.img);
        if (!seen.has(imgUrl)) {
          list.push(imgUrl);
          seen.add(imgUrl);
        }
      });
    }

    if (list.length === 0) {
      list.push('/placeholder.png');
    }

    return list;
  }, [bag]);

  const nextImage = () => {
    if (images.length <= 1) return;
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    if (images.length <= 1) return;
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Məhsul yüklənir..." />
      </div>
    );
  }

  if (error || !bag) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" data-testid="bag-detail-error">
        <p className="text-red-500 mb-4">{error || 'Məhsul tapılmadı'}</p>
        <Link
          to="/bags"
          className="inline-flex items-center gap-2 text-brand-primary font-medium"
          data-testid="back-to-bags"
        >
          <ArrowLeft className="w-4 h-4" />
          Çantalara qayıt
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="bag-detail-page">
      {/* Top back */}
      <div className="py-4 border-b border-brand-secondary/30 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/bags"
            className="inline-flex items-center gap-2 text-brand-muted hover:text-brand-primary transition-colors"
            data-testid="breadcrumb-back"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Çantalara qayıt</span>
          </Link>
        </div>
      </div>

      {/* Detail */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Gallery */}
            <div className="space-y-4">
              <div className="relative aspect-[3/4] bg-white rounded-3xl overflow-hidden shadow-warm">
                <img
                  src={images[currentImageIndex]}
                  alt={bag.bagAd || 'Çanta'}
                  className="w-full h-full object-cover"
                  data-testid="bag-main-image"
                  onError={(e) => {
                    e.currentTarget.src = '/placeholder.png';
                  }}
                />

                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                      data-testid="prev-image-btn"
                    >
                      <ChevronLeft className="w-6 h-6 text-brand-brown" />
                    </button>

                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                      data-testid="next-image-btn"
                    >
                      <ChevronRight className="w-6 h-6 text-brand-brown" />
                    </button>

                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/90 px-4 py-2 rounded-full shadow-lg">
                      <span className="text-sm font-medium text-brand-brown">
                        {currentImageIndex + 1} / {images.length}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {images.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {images.map((img, index) => (
                    <button
                      key={`${img}-${index}`}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-24 rounded-xl overflow-hidden border-2 transition-all ${
                        currentImageIndex === index
                          ? 'border-brand-primary'
                          : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <img
                        src={img}
                        alt={`${bag.bagAd} ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder.png';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="lg:py-8">
              {bag.bagCategory && (
                <span
                  className="inline-flex items-center gap-2 bg-brand-accent/30 text-brand-primary text-sm font-medium px-4 py-2 rounded-full"
                  data-testid="bag-category"
                >
                  <ShoppingBag className="w-4 h-4" />
                  {bag.bagCategory}
                </span>
              )}

              <h1 className="font-serif text-4xl md:text-5xl text-brand-brown mt-4" data-testid="bag-name">
                {bag.bagAd}
              </h1>

              {bag.bagNov && (
                <p className="text-brand-muted text-lg mt-2" data-testid="bag-type">
                  {bag.bagNov}
                </p>
              )}

              <p className="font-serif text-4xl text-brand-primary mt-6" data-testid="bag-price">
                {Number(bag.bagPrice || 0).toFixed(2)} ₼
              </p>

              <div className="flex gap-4 mt-8">
                <Link
                  to="/contact"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-primary to-[#E08E73] text-white px-8 py-4 font-medium hover:shadow-warm-lg hover:scale-105 transition-all duration-300"
                  data-testid="contact-for-purchase"
                >
                  Sifariş et
                </Link>

                <button className="w-14 h-14 rounded-full border-2 border-brand-secondary flex items-center justify-center text-brand-muted hover:text-brand-primary hover:border-brand-primary transition-colors">
                  <Heart className="w-6 h-6" />
                </button>

                <button className="w-14 h-14 rounded-full border-2 border-brand-secondary flex items-center justify-center text-brand-muted hover:text-brand-primary hover:border-brand-primary transition-colors">
                  <Share2 className="w-6 h-6" />
                </button>
              </div>

              <div className="mt-10 pt-8 border-t border-brand-secondary/50">
                <h3 className="font-serif text-xl text-brand-brown mb-4">
                  Məhsul haqqında
                </h3>
                <p className="text-brand-muted leading-relaxed">
                  Bu əl işi çanta premium keyfiyyətli materiallardan hazırlanmışdır.
                  Hər tikişdə ustalıq və zəriflik hiss olunur. Gündəlik istifadə və xüsusi
                  kombinlər üçün uyğundur.
                </p>
              </div>

              <div className="mt-8 pt-8 border-t border-brand-secondary/50">
                <h3 className="font-serif text-xl text-brand-brown mb-4">
                  Ətraflı məlumat
                </h3>

                <ul className="space-y-3">
                  <li className="flex justify-between py-3 border-b border-brand-secondary/30">
                    <span className="text-brand-muted">Ad</span>
                    <span className="text-brand-brown font-medium">{bag.bagAd}</span>
                  </li>

                  {bag.bagNov && (
                    <li className="flex justify-between py-3 border-b border-brand-secondary/30">
                      <span className="text-brand-muted">Model</span>
                      <span className="text-brand-brown font-medium">{bag.bagNov}</span>
                    </li>
                  )}

                  {bag.bagCategory && (
                    <li className="flex justify-between py-3 border-b border-brand-secondary/30">
                      <span className="text-brand-muted">Kateqoriya</span>
                      <span className="text-brand-brown font-medium">{bag.bagCategory}</span>
                    </li>
                  )}

                  <li className="flex justify-between py-3 border-b border-brand-secondary/30">
                    <span className="text-brand-muted">İstehsal</span>
                    <span className="text-brand-brown font-medium">100% Əl işi</span>
                  </li>

                  <li className="flex justify-between py-3 border-b border-brand-secondary/30">
                    <span className="text-brand-muted">Qiymət</span>
                    <span className="text-brand-brown font-medium">
                      {Number(bag.bagPrice || 0).toFixed(2)} ₼
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BagDetailPage;